import React, { useMemo } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  LinearProgress,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RouteIcon from "@mui/icons-material/Route";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import { formatDate } from "../../../gerencia/report/orderService/utils/formatters";

const formatCurrency = (value) => {
  return `S/ ${Number(value || 0).toFixed(2)}`;
};

const getPercent = (value, total) => {
  if (!total) return 0;
  return Math.round((Number(value || 0) / Number(total)) * 100);
};

const MetricCard = ({
  label,
  value,
  helper,
  icon,
  color = "primary",
  progress,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 3,
      height: "100%",
      bgcolor: "background.paper",
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="overline" color="text.secondary" fontWeight={900}>
        {label}
      </Typography>

      {icon && (
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            bgcolor: `${color}.50`,
            color: `${color}.main`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      )}
    </Stack>

    <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
      {value}
    </Typography>

    {helper && (
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    )}

    {typeof progress === "number" && (
      <Box mt={1.2}>
        <LinearProgress
          variant="determinate"
          value={Math.min(progress, 100)}
          color={color}
          sx={{ height: 7, borderRadius: 99 }}
        />
      </Box>
    )}
  </Paper>
);

const InfoBlock = ({ label, value }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography fontWeight={700}>{value || "No especificado"}</Typography>
  </Box>
);

const ServiceOrderSummaryCards = ({ service, travelers = [], user, permissions }) => {
  //const adminID = Number(process.env.REACT_APP_ROL_ADMIN_ID);
  //const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
  //const gerenciaID = Number(process.env.REACT_APP_ROL_GERENTE_ID);

  //const canViewFinancialData = [adminID, administradorID, gerenciaID].includes( user?.RolID );

  const canViewFinancialData = !!permissions?.canViewFinancialData;

  const metrics = useMemo(() => {
    const total = travelers.length;

    const subida = travelers.filter((t) => t.ordserdettra === "IDA").length;
    const bajada = travelers.filter((t) => t.ordserdettra === "VUELTA").length;

    const viajaron = travelers.filter((t) => t.estadoreporte === "VIAJO").length;
    const noViajaron = travelers.filter(
      (t) => t.estadoreporte === "NO_VIAJO"
    ).length;

    const reservados = travelers.filter(
      (t) => t.estadoreporte === "RESERVADO"
    ).length;

    const sinManifiesto = travelers.filter((t) => !t.manid).length;

    const noAsignados = travelers.filter(
      (t) => t.ordserdetestpas === "NO_ASIGNADO"
    ).length;

    const manifiestos = new Set(
      travelers.filter((t) => t.manid).map((t) => t.manid)
    );

    const subidaViajaron = travelers.filter(
      (t) => t.ordserdettra === "IDA" && t.estadoreporte === "VIAJO"
    ).length;

    const subidaNoViajaron = travelers.filter(
      (t) => t.ordserdettra === "IDA" && t.estadoreporte === "NO_VIAJO"
    ).length;

    const bajadaViajaron = travelers.filter(
      (t) => t.ordserdettra === "VUELTA" && t.estadoreporte === "VIAJO"
    ).length;

    const bajadaNoViajaron = travelers.filter(
      (t) => t.ordserdettra === "VUELTA" && t.estadoreporte === "NO_VIAJO"
    ).length;

    const invitados = travelers.filter(
      (t) => t.ordserdetestpas === "INVITADO"
    ).length;

    const invitadosSubida = travelers.filter(
      (t) =>
        t.ordserdettra === "IDA" &&
        t.ordserdetestpas === "INVITADO"
    ).length;

    const invitadosBajada = travelers.filter(
      (t) =>
        t.ordserdettra === "VUELTA" &&
        t.ordserdetestpas === "INVITADO"
    ).length;

    return {
      total,
      subida,
      bajada,
      viajaron,
      noViajaron,
      reservados,
      sinManifiesto,
      noAsignados,
      unidades: manifiestos.size,
      avance: getPercent(viajaron, total),
      pendiente: getPercent(noAsignados + reservados + sinManifiesto, total),
      subidaViajaron,
      subidaNoViajaron,
      bajadaViajaron,
      bajadaNoViajaron,
      invitados,
      invitadosSubida,
      invitadosBajada,
    };
  }, [travelers]);

  return (
    <Box mb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1.5}
            >
              <Typography variant="h6" fontWeight={900}>
                Información del servicio
              </Typography>
            </Stack>

            <InfoBlock label="Proyecto" value={service?.pronom} />

            <Divider sx={{ my: 1.5 }} />

            <InfoBlock label="Tipo de servicio" value={service?.serpronom} />

            {canViewFinancialData && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <InfoBlock label="Transporte de" value={service?.ordsercabtip} />
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
            }}
          >
            <Typography variant="h6" fontWeight={900} mb={1.5}>
              Fechas y condiciones
            </Typography>

            <InfoBlock
              label="Fecha de inicio"
              value={formatDate(service?.ordsercabfec)}
            />

            <Divider sx={{ my: 1.5 }} />

            <InfoBlock
              label="Fecha de fin"
              value={formatDate(service?.ordsercabfecfin)}
            />

            {canViewFinancialData && (
              <>
                <Divider sx={{ my: 1.5 }} />

                <InfoBlock
                  label="Precio / Precio base"
                  value={`${formatCurrency(service?.ordsercabpre)} - ${formatCurrency(
                    service?.serpropre
                  )}`}
                />
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <MetricCard
            label="Total pasajeros"
            value={metrics.total}
            helper={`${metrics.subida} subida / ${metrics.bajada} bajada`}
            icon={<GroupsIcon fontSize="small" />}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <MetricCard
            label="Unidades"
            value={metrics.unidades}
            helper="Manifiestos asociados"
            icon={<DirectionsBusIcon fontSize="small" />}
            color="info"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <MetricCard
            label="Viajaron"
            value={metrics.viajaron}
            helper={`${metrics.avance}% del total`}
            icon={<TrendingUpIcon fontSize="small" />}
            color="success"
            progress={metrics.avance}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <MetricCard
            label="No viajaron"
            value={metrics.noViajaron}
            helper={
              metrics.noAsignados > 0
                ? `${metrics.noAsignados} aún no asignados`
                : "Sin pendientes por asignación"
            }
            icon={<PersonOffIcon fontSize="small" />}
            color="error"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MetricCard
            label="Subida"
            value={metrics.subida}
            helper={`${metrics.subidaViajaron} viajaron (${metrics.invitadosSubida} invitados) / ${metrics.subidaNoViajaron} no viajaron`}
            icon={<RouteIcon fontSize="small" />}
            color="primary"
            progress={getPercent(metrics.subidaViajaron, metrics.subida)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MetricCard
            label="Bajada"
            value={metrics.bajada}
            helper={`${metrics.bajadaViajaron} viajaron (${metrics.invitadosBajada} invitados) / ${metrics.bajadaNoViajaron} no viajaron`}
            icon={<AltRouteIcon fontSize="small" />}
            color="secondary"
            progress={getPercent(metrics.bajadaViajaron, metrics.bajada)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ServiceOrderSummaryCards;